import { __rest } from 'tslib';
import useSWR, { useSWRInfinite } from 'swr';
import { normalizePageBuilderToDiscoveryParams, cleanObject } from './service.es.js';
import { paramToUrl, getObjFromQueryString, cookFilter, cookSearchIntent } from './helpers.es.js';
import { PageTypeEnum } from './types.es.js';

const useDiscoverySearch = (discoveryService, params, location) => {
    const _a = params || {}, { sorting } = _a, searchParams = __rest(_a, ["sorting"]);
    const normalizedParams = normalizePageBuilderToDiscoveryParams(searchParams);
    const url = paramToUrl(normalizedParams, discoveryService.getBaseUrl());
    const terminalCode = normalizedParams === null || normalizedParams === void 0 ? void 0 : normalizedParams.terminalCode;
    const pos365StoreCode = normalizedParams === null || normalizedParams === void 0 ? void 0 : normalizedParams.pos365StoreCode;
    const { data, error } = useSWR(params && (!!terminalCode || !pos365StoreCode)
        ? [`${url.href}`, url.search]
        : null, async (_url, params) => {
        return await discoveryService.search(Object.assign(Object.assign({}, getObjFromQueryString(params)), { location,
            sorting }));
    });
    const result = data === null || data === void 0 ? void 0 : data.result;
    return {
        result: {
            products: (result === null || result === void 0 ? void 0 : result.products) || [],
            filter: (result === null || result === void 0 ? void 0 : result.filter) || {},
            pagination: (data === null || data === void 0 ? void 0 : data.pagination) || {},
            cookedFilters: cookFilter(result === null || result === void 0 ? void 0 : result.filter, normalizedParams, result === null || result === void 0 ? void 0 : result.masterCategories),
            searchIntents: cookSearchIntent(result),
            suggestedKeyword: result === null || result === void 0 ? void 0 : result.suggestedKeyword,
        },
        isLoading: !params ? false : !error && !data,
        isError: error,
    };
};
const useInfiniteDiscoverySearch = (discoveryService, params, location) => {
    const url = paramToUrl(params, discoveryService.getBaseUrl());
    const { data, error, size = 1, setSize = () => { }, } = useSWRInfinite((index) => [
        `${url.href}&_page=${index + 1}`,
        `${url.search}&_page=${index + 1}`,
    ], async (url, params) => {
        var _a, _b;
        const res = await discoveryService.search(Object.assign(Object.assign({}, getObjFromQueryString(params)), { location }));
        return {
            products: ((_a = res === null || res === void 0 ? void 0 : res.result) === null || _a === void 0 ? void 0 : _a.products) || [],
            searchIntents: cookSearchIntent(res === null || res === void 0 ? void 0 : res.result),
            suggestedKeyword: (_b = res === null || res === void 0 ? void 0 : res.result) === null || _b === void 0 ? void 0 : _b.suggestedKeyword,
        };
    });
    const isLoading = !data && !error;
    const isEmpty = data && data[0].products.length === 0;
    const hasMore = !(isEmpty ||
        (data &&
            data[data.length - 1] &&
            data[data.length - 1].products.length < (params._limit || 10)));
    const products = data
        ? data.reduce((acc, curr) => {
            return [...acc, ...curr.products];
        }, [])
        : [];
    const cookedData = data === null || data === void 0 ? void 0 : data[0];
    return {
        isEmpty,
        isLoading,
        hasMore,
        page: size,
        setPage: setSize,
        products,
        searchIntents: (cookedData === null || cookedData === void 0 ? void 0 : cookedData.searchIntents) ? cookedData.searchIntents : null,
        suggestedKeyword: cookedData === null || cookedData === void 0 ? void 0 : cookedData.suggestedKeyword,
    };
};
const useDiscoveryProduct = (discoveryService, sku, terminal, location, callFromDiscovery = false) => {
    var _a;
    const { data, error } = useSWR(!!sku && !!terminal
        ? [`${discoveryService.getBaseUrl}/product`, sku, terminal, location]
        : null, async (_, sku, terminal, location) => {
        return await discoveryService.getProduct({ sku, terminalCode: terminal, location }, callFromDiscovery);
    });
    return {
        result: {
            product: ((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.product) || null,
        },
        isLoading: !error && !data,
        isError: !!error,
    };
};
const usePersonalizeBlocks = (discoveryService, blockParams, terminal, transformBlock, user) => {
    var _a, _b;
    const { pageTypeEnum, category, seller, brand, sellerCategory } = blockParams;
    const phoneNumber = user === null || user === void 0 ? void 0 : user.telephone;
    const userId = user === null || user === void 0 ? void 0 : user.userId;
    const params = cleanObject(Object.assign({ terminalCode: terminal, phoneNumber,
        userId }, blockParams));
    const conditionFetching = terminal &&
        (pageTypeEnum === PageTypeEnum.HOME ||
            PageTypeEnum.NOTIFICATION ||
            (pageTypeEnum === PageTypeEnum.CATEGORY &&
                (category || sellerCategory)) ||
            (pageTypeEnum === PageTypeEnum.BRAND && brand) ||
            (pageTypeEnum === PageTypeEnum.SELLER && seller));
    const { data, error } = useSWR(conditionFetching
        ? [`${discoveryService.getBaseUrl}/blocks`, JSON.stringify(params)]
        : null, async (_, paramsJson) => {
        return await discoveryService.getBlock(JSON.parse(paramsJson));
    });
    return {
        result: (((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.blocks) || []).map(transformBlock),
        seoInfo: ((_b = data === null || data === void 0 ? void 0 : data.result) === null || _b === void 0 ? void 0 : _b.seoInfo) || {},
        isLoading: !error && !data,
        isError: error,
    };
};
const useSkus = (discoveryService, skus, terminal, location) => {
    const { data, error } = useSWR(skus
        ? [
            `${discoveryService.getBaseUrl}/products`,
            skus.join(','),
            terminal,
            location,
        ]
        : null, async (_, skus, terminal, location) => {
        return await discoveryService.getProductDetailBySkus({
            skus,
            terminalCode: terminal,
            location,
        });
    });
    return {
        result: {
            products: data || [],
        },
        isLoading: !skus ? false : !error && !data,
        isError: error,
    };
};

export { useDiscoveryProduct, useDiscoverySearch, useInfiniteDiscoverySearch, usePersonalizeBlocks, useSkus };
